import { useEffect, useState } from "react";
import { createStore } from "zustand/vanilla";
import { unifiedProjectSocketManager } from "../../../../../socket";

// ═════════════════════════════════════════════════════════════════════════════
// Annual OKR — Live Review socket store
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES.
//
//   10x-coach-frontend/src/components/CoachPanel/AnnualOkr/
//     AnnualOkrLiveReview/_socket/AnnualOkrLiveReviewSocketStore.js
//   10x-frontend/src/components/UserPanel/AnnualOkr/
//     AnnualOkrLiveReview/_socket/AnnualOkrLiveReviewSocketStore.js
//
// Live Review is a room with two different logged-in people in it — a CEO in
// one app, their coach in another — receiving the SAME `details` snapshot and
// the same delta events. Any disagreement between their two readers shows up as
// the two of them seeing different things while sitting in a meeting together,
// which is close to the worst possible place for a subtle drift.
//
// There is no shared package between the repos, so the defence is that these
// files are byte-for-byte the same and `diff` is the test. Both sit at the same
// folder depth precisely so the relative import above is identical too. **Any
// change here must be applied to both copies in the same pass.** If an
// app-specific branch is ever needed, pass it in as an argument —
// `viewerRole` on `connect()` is the existing example, and deliberately the
// only one. This mirrors the Execution Blueprint's `LiveReviewSocketStore.js`,
// which carries the same warning for the same reason.
//
// ⚠️ ITS OWN NAMESPACE. §7 puts Annual OKR on `/annual-okr-live-review`, not on
// the Execution Blueprint's `/live-review`. That settles an open question from
// the planning pass: EXW's byte-identical store is untouched by any of this,
// and the two features cannot deliver each other's events.
//
// Contract: `docs/annual-okr/20-fe-integration-guide.md` §7 (7.1 client events,
// 7.2 server events, 7.4 editing rules, 7.5 locking, 7.6 error codes).
// ═════════════════════════════════════════════════════════════════════════════

// The namespace key registered in both repos' `src/socket.js` manager switches.
// Not the URL — the manager maps this to the right `io()` instance.
export const ANNUAL_OKR_LIVE_REVIEW_NAMESPACE = "annual-okr-live-review";

// ─── Wire vocabulary (§7.1) ──────────────────────────────────────────────────
// Client → server.
export const AOK_LR_EMIT = {
  // Always first. Every other event checks `requireJoined` and answers
  // NOT_AUTHORIZED ("Join the session first") otherwise.
  JOIN: "join_annual_okr_live_review",
  // ⚠️ THE ONE EVENT HERE THAT IS NOT PART OF §7. Added 2026-09-24: the owner
  // picker needs the company's assignable users, and §7.2's state payload
  // carries none — so the Add-an-Objective form could only ever offer people
  // who already owned an Objective on this board.
  //
  // Payload is `{ chat_id }` — the run's `unique_id`, which is also the chat's
  // id (§5). The backend accepts an empty `{}` too and echoes whatever it is
  // given, but sending the id keeps the request attributable to a run in the
  // server's logs, and the coach side needs it.
  //
  // It answers on `annual_okr_roster`, the same event the interview chat gets.
  GET_ROSTER: "get_annual_okr_roster",
  // { objective_statement, owner_user_id, key_results: [{statement, unit,
  // target}] } — 1–3 KRs. Always freestanding, lands CONFIRMED immediately
  // with an auto-ACCEPTED review row, and appends at the end of the list.
  ADD_OBJECTIVE: "add_annual_okr_objective",
  // { annual_objective_id, objective_statement?, key_results?: [{sequence,
  // statement?, unit?, target?}] } — a direct overwrite. §7.4: no history, no
  // versioning, no confirmation step.
  EDIT_OBJECTIVE: "edit_annual_okr_objective",
  // { annual_objective_id } — a SOFT delete that also marks its own review row
  // discussed (so it cannot block the lock gate) and hard-deletes its
  // OBJECTIVE-scoped comments. Refused with OBJECTIVE_FLOOR_REACHED when it
  // would empty the run.
  DELETE_OBJECTIVE: "delete_annual_okr_objective",
  // { annual_objective_id, status: "accepted" | "to_discuss" }
  SET_OBJECTIVE_STATUS: "set_objective_review_status",
  // { remark_type: "comment" | "objective", id } — ONE-WAY. There is no
  // "un-discuss", which is what makes the lock gate monotonic.
  MARK_REMARK_DISCUSSED: "mark_remark_discussed",
  // No payload. ⚠️ TERMINAL. §7.5: no socket events fire for this run
  // afterwards, so the screen must navigate away rather than wait for more.
  LOCK: "lock_annual_okr",
};

// Server → client (§7.2).
export const AOK_LR_ON = {
  // Reply to a successful `join`, to the joining client only.
  DETAILS: "annual_okr_live_review_details",
  // The answer to GET_ROSTER above: `{ chat_id, roster: [{ user_id, name,
  // role_id }] }`. `chat_id` is whatever the request sent, echoed back — this
  // store ignores it, since only one roster request is ever in flight.
  ROSTER: "annual_okr_roster",
  // The four below broadcast to the WHOLE room — including back to the client
  // that caused them. That is deliberate and is why nothing here is applied
  // optimistically: the echo is the confirmation, and applying twice would be
  // the only way to get out of sync.
  OBJECTIVE_ADDED: "objective_added",
  OBJECTIVE_UPDATED: "objective_updated",
  OBJECTIVE_DELETED: "objective_deleted",
  OBJECTIVE_STATUS_UPDATED: "objective_status_updated",
  // ⚠️ `remarks_updated`, NOT `remark_discussed`. This store guessed the latter
  // and it does not exist — which is why marking a remark discussed appeared to
  // do nothing. It fires after a status change OR a discuss-mark, and carries
  // the whole remarks block.
  REMARKS_UPDATED: "remarks_updated",
  // §7.2: on join AND on disconnect, to the whole room.
  PRESENCE_UPDATED: "presence_update",
  LOCKED: "annual_okr_locked",
  // ⚠️ THE ONE EVENT THAT IS NOT SENT TO THE REVIEW ROOM. §7.2 delivers it to
  // the CEO's PERSONAL `user_${user_id}` room, because the coach fires it from
  // a REST call (§6.8 `live-review/start`) at a moment when the review room
  // does not exist yet — nobody has joined.
  //
  // So it is NOT consumed by this store's `connect`, which joins the room and
  // is only used by the live review screen itself. It is listened for by the
  // CEO's coach-waiting screen through `useLiveReviewStartedWatch`, which
  // connects to the same namespace and deliberately never joins.
  REVIEW_STARTED: "annual_okr_live_review_started",
  ERROR: "error",
};

// §7.6. Branch on `code`, never on `message` — wording may change.
export const AOK_LR_ERROR = {
  LIVE_REVIEW_NOT_STARTED: "live_review_not_started",
  NOT_AUTHORIZED: "not_authorized",
  OBJECTIVE_NOT_FOUND: "objective_not_found",
  WRONG_STATE: "wrong_state",
  REMARKS_REMAINING: "remarks_remaining",
  REMARK_NOT_FOUND: "remark_not_found",
  ALREADY_LOCKED: "already_locked",
  OWNER_REQUIRED: "owner_required",
  INVALID_OWNER: "invalid_owner",
  INVALID_KEY_RESULTS: "invalid_key_results",
  OBJECTIVE_FLOOR_REACHED: "objective_floor_reached",
};

// §7: a rejected handshake disconnects rather than erroring, so "connected then
// gone again almost immediately, never joined" is the signature. Same window
// EXW's store uses.
const AUTH_FAILURE_WINDOW_MS = 3000;

let connectedAt = null;

const INITIAL_STATE = {
  // The run being joined. Held so `join()` can re-fire on every reconnect.
  uniqueId: null,
  // Connection lifecycle, kept separate from the data so a reconnect can show
  // "reconnecting" over the last good snapshot rather than blanking it.
  connected: false,
  joined: false,
  joining: false,
  // The last `error` payload, `{ code, message }`. Held rather than toasted so
  // the screen can decide — a floor-reached needs to sit next to the row it
  // refused, where a not-authorized has to take over the screen.
  error: null,

  // The §7.2 DETAILS snapshot, held whole.
  details: null,
  objectives: [],
  remarks: [],
  remarksRemaining: 0,
  // §7.3: ALWAYS a 2-item array with both roles present, even before either has
  // joined — so the strip renders two rows unconditionally and never has to
  // handle a missing slot.
  participants: [],
  // The company's assignable users, when the state payload carries them — see
  // the note in the DETAILS handler.
  roster: [],
  annualOkrState: null,
  lockedAt: null,

  // Which party this client is. The ONE app-specific input — see the header.
  viewerRole: null,
};

const annualOkrLiveReviewStore = createStore((set, get) => ({
  ...INITIAL_STATE,

  // ── Connect + join ────────────────────────────────────────────────────────
  // §7: identity is resolved by a namespace middleware BEFORE the connection is
  // accepted, so `join` can be emitted immediately on `connect` — there is no
  // separate auth-ready wait here, unlike the interview chat socket.
  connect: ({ uniqueId, viewerRole }) => {
    set({ viewerRole, error: null });
    const socket = unifiedProjectSocketManager.connect(
      ANNUAL_OKR_LIVE_REVIEW_NAMESPACE,
    );
    if (!socket) return;

    set({ uniqueId });

    // ⚠️ STRIP STALE LISTENERS FIRST. The manager keeps the socket instance
    // alive across mounts, so without this a remount stacks a second copy of
    // every handler and each event applies twice. Written as explicit lines
    // rather than a loop, deliberately: a loop would silently cover a future
    // event and hide the fact that adding one has a second obligation here.
    socket.off("connect");
    socket.off("disconnect");
    socket.off("connect_error");
    socket.off(AOK_LR_ON.DETAILS);
    socket.off(AOK_LR_ON.OBJECTIVE_ADDED);
    socket.off(AOK_LR_ON.OBJECTIVE_UPDATED);
    socket.off(AOK_LR_ON.OBJECTIVE_DELETED);
    socket.off(AOK_LR_ON.OBJECTIVE_STATUS_UPDATED);
    socket.off(AOK_LR_ON.REMARKS_UPDATED);
    socket.off(AOK_LR_ON.PRESENCE_UPDATED);
    socket.off(AOK_LR_ON.LOCKED);
    socket.off(AOK_LR_ON.ERROR);

    socket.on("connect", () => {
      connectedAt = Date.now();
      set({ connected: true, error: null });
      // ⚠️ RE-JOIN ON EVERY CONNECT, not just the first. A reconnect drops us
      // out of the room server-side, and §7.1 says every other event checks
      // `requireJoined` — so without this a recovered socket looks connected
      // and silently receives nothing, which is exactly the "connected but no
      // data" failure.
      get().join();
    });

    socket.on("disconnect", (reason) => {
      const wasJoined = get().joined;
      const elapsed = connectedAt ? Date.now() - connectedAt : Infinity;
      set({ connected: false, joined: false });

      // §7's handshake is rejected by disconnecting, with no `error` event
      // first — so this is the only place an auth failure can be detected.
      if (!wasJoined && elapsed < AUTH_FAILURE_WINDOW_MS) {
        // eslint-disable-next-line no-console
        console.error(
          "[AnnualOkrLiveReview] Disconnected immediately after connecting " +
            "without joining. Per §7 that is how a rejected handshake " +
            "presents. The handshake sends the token from src/socket.js's " +
            "`extraHeaders`; §7 asks for the same Bearer pattern as §3. If " +
            "this persists, check that form first.",
          { reason },
        );
        set({
          joining: false,
          error: {
            code: AOK_LR_ERROR.NOT_AUTHORIZED,
            message:
              "Couldn't join the live review — the session refused the connection.",
          },
        });
      }
    });

    socket.on("connect_error", (err) => {
      set({
        joining: false,
        error: {
          code: "connect_error",
          message: err?.message || "Couldn't connect to the live review.",
        },
      });
    });

    socket.off(AOK_LR_ON.DETAILS);
    socket.on(AOK_LR_ON.DETAILS, (raw) => {
      const data = parse(raw);
      if (!data) return;
      // ⚠️ THE AUTHORITATIVE STATE, and not only on join. §7.2: add / edit /
      // delete each emit their narrow delta AND THEN a full `DETAILS`
      // broadcast to the room (`broadcastFullResync`), and the contract says
      // the simplest-correct FE behaviour is to re-render entirely from this
      // rather than hand-rolling delta patches. So this handler is the one
      // that matters; the delta handlers below are a first paint before it
      // lands, never the source of truth.
      set({
        joining: false,
        joined: true,
        details: data,
        objectives: data.objectives || [],
        remarks: data.remarks?.remarks || [],
        // Same rule as `remarks_updated` below: a missing count is never 0.
        remarksRemaining:
          data.remarks?.remarks_remaining ??
          (data.remarks?.remarks || []).filter((remark) => !remark.discussed)
            .length,
        participants: data.participants || [],
        annualOkrState: data.annual_okr_state || null,
        lockedAt: data.locked_at || null,
      });
    });

    // ── The owner roster ────────────────────────────────────────────────────
    // Answer to `GET_ROSTER`. It is NOT part of §7's state snapshot — the
    // live-review payload carries no roster — so it arrives on its own and is
    // stored on its own.
    //
    // ⚠️ AN EMPTY ROSTER IS A REAL ANSWER, not a failure: it means nobody in
    // the company is assignable. The composer says so in the picker rather
    // than showing a spinner forever.
    socket.off(AOK_LR_ON.ROSTER);
    socket.on(AOK_LR_ON.ROSTER, (raw) => {
      const data = parse(raw);
      if (!data) return;
      set({ roster: Array.isArray(data.roster) ? data.roster : [] });
    });

    // ── Deltas ──────────────────────────────────────────────────────────────
    // Each broadcasts to the whole room, including the sender. Applied on
    // arrival and never optimistically — see the note on AOK_LR_ON.
    socket.off(AOK_LR_ON.OBJECTIVE_ADDED);
    socket.on(AOK_LR_ON.OBJECTIVE_ADDED, (raw) => {
      const data = parse(raw);
      if (!data) return;
      // §7.4: a live-added Objective appends at the END of the list, so this
      // pushes rather than sorting — the server's order is the order.
      set((state) => ({
        objectives: [...state.objectives, data.objective || data],
      }));
    });

    socket.off(AOK_LR_ON.OBJECTIVE_UPDATED);
    socket.on(AOK_LR_ON.OBJECTIVE_UPDATED, (raw) => {
      const data = parse(raw);
      const updated = data?.objective || data;
      if (!updated) return;
      set((state) => ({
        objectives: state.objectives.map((objective) =>
          sameId(objective.annual_objective_id, updated.annual_objective_id)
            ? { ...objective, ...updated }
            : objective,
        ),
      }));
    });

    socket.off(AOK_LR_ON.OBJECTIVE_DELETED);
    socket.on(AOK_LR_ON.OBJECTIVE_DELETED, (raw) => {
      const data = parse(raw);
      if (!data) return;
      // §7.3: the delete delta is `{ annual_objective_id, deleted_by,
      // deleted_at }`. The row is dropped here so the list reacts instantly,
      // and the full `DETAILS` resync that follows carries the real
      // consequences — including any remark the delete cleared. An earlier
      // version recomputed that cascade locally, which was a guess at server
      // behaviour the resync already reports.
      const id = data.annual_objective_id;
      set((state) => ({
        objectives: state.objectives.filter(
          (objective) => !sameId(objective.annual_objective_id, id),
        ),
      }));
    });

    socket.off(AOK_LR_ON.OBJECTIVE_STATUS_UPDATED);
    socket.on(AOK_LR_ON.OBJECTIVE_STATUS_UPDATED, (raw) => {
      const data = parse(raw);
      if (!data) return;
      set((state) => ({
        objectives: state.objectives.map((objective) =>
          sameId(objective.annual_objective_id, data.annual_objective_id)
            ? { ...objective, review_status: data.status }
            : objective,
        ),
      }));
    });

    socket.off(AOK_LR_ON.REMARKS_UPDATED);
    socket.off(AOK_LR_ON.PRESENCE_UPDATED);
    socket.on(AOK_LR_ON.REMARKS_UPDATED, (raw) => {
      const data = parse(raw);
      if (!data) return;
      // §7.2: status changes and discuss-marks get NO full resync — this event
      // (with `objective_status_updated`) is the complete picture for them. It
      // carries the whole remarks block, so it is applied wholesale rather than
      // patched row by row; `remarks_remaining` is the server's own count and
      // is never recomputed here, because it is the lock gate and the two
      // people in the room must be counting the same thing.
      const block = data.remarks ? data : { remarks: data };
      const list = Array.isArray(block.remarks?.remarks)
        ? block.remarks.remarks
        : Array.isArray(block.remarks)
          ? block.remarks
          : null;
      // ⚠️ NEVER `?? 0`. That fallback is the bug that unlocked the Lock button
      // after ONE accept with two points still open: whenever this event
      // arrived without `remarks_remaining` where it was looked for, the gate
      // read 0 and the header said "Agenda items: 0". A missing count must
      // never mean "nothing left".
      //
      // Order of trust: the server's own count; else count the undiscussed
      // rows in the list the server just sent (both people in the room receive
      // the same list, so they still count the same thing); else keep the last
      // known count. Mirrors EXW's `reduceRemarksUpdated`, which only patches
      // the count when one is present.
      const serverCount =
        block.remarks?.remarks_remaining ?? data.remarks_remaining;
      set((state) => ({
        // A counts-only payload leaves the list alone rather than blanking
        // the agenda — EXW's rule too.
        remarks: list ?? state.remarks,
        remarksRemaining:
          Number.isFinite(Number(serverCount)) && serverCount !== null
            ? Number(serverCount)
            : list
              ? list.filter((remark) => !remark.discussed).length
              : state.remarksRemaining,
      }));
    });

    socket.on(AOK_LR_ON.PRESENCE_UPDATED, (raw) => {
      const data = parse(raw);
      if (!data) return;
      set({ participants: data.participants || data || [] });
    });

    socket.off(AOK_LR_ON.LOCKED);
    socket.on(AOK_LR_ON.LOCKED, (raw) => {
      const data = parse(raw);
      // ⚠️ TERMINAL (§7.5). No further events fire for this run, so the screen
      // reads `annualOkrState === "LOCKED"` and navigates away rather than
      // waiting for anything else.
      set({
        annualOkrState: data?.annual_okr_state || "LOCKED",
        lockedAt: data?.locked_at || null,
      });
    });

    socket.off(AOK_LR_ON.ERROR);
    socket.on(AOK_LR_ON.ERROR, (raw) => {
      const data = parse(raw) || {};
      set({ error: { code: data.code || null, message: data.message || "" } });
      // A failed join leaves `joining` stuck otherwise, and the screen would
      // spin forever on a run that has not started.
      if (
        data.code === AOK_LR_ERROR.LIVE_REVIEW_NOT_STARTED ||
        data.code === AOK_LR_ERROR.NOT_AUTHORIZED
      ) {
        set({ joining: false, joined: false });
      }
    });

    // ⚠️ THE ALREADY-CONNECTED CASE. The manager keeps sockets alive across
    // mounts, so on a second visit `connect()` returns a socket that is ALREADY
    // open — the "connect" handler above will never fire, and without this the
    // screen sits connected, unjoined and empty forever. That is the exact
    // "socket is up but no data arrives" failure.
    if (socket.connected) {
      connectedAt = Date.now();
      set({ connected: true });
      get().join();
    }
  },

  // ⚠️ `join` MUST SUCCEED BEFORE ANYTHING ELSE (§7.1): every other event
  // checks `requireJoined` and answers NOT_AUTHORIZED otherwise. Emitted with
  // an ack so a refusal clears the spinner even when the paired `error` event
  // is what carries the reason.
  join: () => {
    const { uniqueId } = get();
    const socket = unifiedProjectSocketManager.getSocket(
      ANNUAL_OKR_LIVE_REVIEW_NAMESPACE,
    );
    if (!socket || !uniqueId) return;
    set({ joining: true });
    socket.emit(AOK_LR_EMIT.JOIN, { unique_id: uniqueId }, (ack) => {
      if (ack && ack.acknowledgement === false) {
        set({ joining: false });
        return;
      }
      // `joined` is confirmed by DETAILS arriving, not by the ack alone — the
      // ack says the emit was accepted, the snapshot says the room is real.
      set({ joining: false });

      // ⚠️ ASKED FOR AFTER THE JOIN, NOT ON CONNECT. Every other event on this
      // namespace is refused with NOT_AUTHORIZED until the room is joined, so
      // firing this earlier would answer nothing. One request per join is
      // enough — the roster does not change mid-meeting, and a reconnect
      // re-runs `join()`, which re-asks.
      socket.emit(AOK_LR_EMIT.GET_ROSTER, { chat_id: uniqueId });
    });
  },

  // ── Emits (§7.1) ──────────────────────────────────────────────────────────
  // All fire-and-forget: the server's broadcast back is the confirmation, so
  // none of these touch local state. `requireJoined` is enforced server-side,
  // and the guard here only avoids emitting into a dead socket.
  addObjective: ({ objectiveStatement, ownerUserId, keyResults }) =>
    emit(AOK_LR_EMIT.ADD_OBJECTIVE, {
      objective_statement: objectiveStatement,
      owner_user_id: ownerUserId,
      key_results: keyResults,
    }),

  editObjective: ({
    annualObjectiveId,
    objectiveStatement,
    keyResults,
    ownerUserId,
  }) =>
    emit(AOK_LR_EMIT.EDIT_OBJECTIVE, {
      annual_objective_id: annualObjectiveId,
      // All three optional — omitted rather than sent as undefined, so a
      // statement-only edit does not look like a request to clear the KRs or
      // unassign the owner.
      ...(objectiveStatement != null
        ? { objective_statement: objectiveStatement }
        : {}),
      ...(keyResults != null ? { key_results: keyResults } : {}),
      // ⚠️ REASSIGNMENT, ADDED 2026-09-24. §7.1's documented payload for this
      // event had no owner field; the backend accepts one now, so the live
      // review can hand an Objective to someone else. Sent only when the
      // picker actually changed (see the caller) — and an id off the roster,
      // or the server refuses the whole call with `invalid_owner`.
      ...(ownerUserId != null ? { owner_user_id: ownerUserId } : {}),
    }),

  deleteObjective: (annualObjectiveId) =>
    emit(AOK_LR_EMIT.DELETE_OBJECTIVE, {
      annual_objective_id: annualObjectiveId,
    }),

  setObjectiveStatus: (annualObjectiveId, status) =>
    emit(AOK_LR_EMIT.SET_OBJECTIVE_STATUS, {
      annual_objective_id: annualObjectiveId,
      status,
    }),

  markRemarkDiscussed: (remarkType, id) =>
    emit(AOK_LR_EMIT.MARK_REMARK_DISCUSSED, { remark_type: remarkType, id }),

  // ⚠️ Gated on `remarksRemaining === 0` server-side, and §7.5 says the FE can
  // disable the button on the same count without waiting for a failed attempt.
  // The guard here is belt-and-braces for a stale count.
  lock: () => {
    if (get().remarksRemaining > 0) return;
    emit(AOK_LR_EMIT.LOCK, undefined);
  },

  clearError: () => set({ error: null }),

  // Hard disconnect, because this namespace is only ever open while the screen
  // is. Leaving it connected would keep the room occupied and keep delivering
  // events to a store nothing is reading.
  disconnect: () => {
    const socket = unifiedProjectSocketManager.getSocket(
      ANNUAL_OKR_LIVE_REVIEW_NAMESPACE,
    );
    if (socket) {
      Object.values(AOK_LR_ON).forEach((event) => socket.off(event));
      socket.off("connect");
      socket.off("disconnect");
    }
    unifiedProjectSocketManager.hardDisconnect(
      ANNUAL_OKR_LIVE_REVIEW_NAMESPACE,
    );
    set(INITIAL_STATE);
  },
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Payloads arrive spread or as a JSON string depending on the emitter, the same
// as on the interview socket. Parsing defensively costs nothing and a silent
// throw inside a socket handler is invisible.
const parse = (raw) => {
  if (raw == null) return null;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    // eslint-disable-next-line no-console
    console.warn("[AnnualOkrLiveReview] unparseable payload", raw);
    return null;
  }
};

// ⚠️ Compared as STRINGS. Ids arrive as numbers from §7's socket payloads and
// as strings from §5.6's, and a screen can hold both at once — the run it
// joined came from a REST read. `==` would do it, but this says why.
const sameId = (a, b) => String(a) === String(b);

const emit = (event, payload) => {
  const socket = unifiedProjectSocketManager.getSocket(
    ANNUAL_OKR_LIVE_REVIEW_NAMESPACE,
  );
  if (!socket) return;
  if (payload === undefined) socket.emit(event);
  else socket.emit(event, payload);
};

// ─── React binding ───────────────────────────────────────────────────────────
// Hand-rolled, matching every other store in both apps. The `prev === next`
// bail-out is what stops an unchanged slice re-rendering.
export const useAnnualOkrLiveReviewStore = (selector) => {
  const [state, setState] = useState(() =>
    selector(annualOkrLiveReviewStore.getState()),
  );
  useEffect(
    () =>
      annualOkrLiveReviewStore.subscribe((s) => {
        const next = selector(s);
        setState((prev) => (prev === next ? prev : next));
      }),
    // Subscribe once. The selector is deliberately not a dep — re-subscribing
    // on every render is the bug this hook exists to avoid.
    [], // eslint-disable-line
  );
  return state;
};

export default annualOkrLiveReviewStore;
